/**
 * Image Export Utilities
 *
 * Functions for converting SVG elements to PNG images for sharing
 */

/**
 * Convert an SVG element to a PNG blob
 * @param {SVGElement} svgElement - The SVG DOM element to convert
 * @param {number} width - Output width in pixels (default: 1200)
 * @param {number} height - Output height in pixels (default: 630)
 * @param {number} scale - Scale factor for higher resolution (default: 2 for retina)
 * @returns {Promise<Blob>} PNG image blob
 */
export async function svgToPngBlob(
  svgElement: SVGElement,
  width: number = 1200,
  height: number = 630,
  scale: number = 2,
): Promise<Blob> {
  // Get the SVG markup
  const svgData = new XMLSerializer().serializeToString(svgElement);

  // Create a blob from the SVG
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  // Create an image element
  const img = new Image();
  img.width = width * scale;
  img.height = height * scale;

  // Load the SVG into the image
  const imageLoaded = new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });

  await imageLoaded;

  // Create a canvas and draw the image
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  // Enable better image quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw the SVG image (no white background - SVG has its own)
  ctx.drawImage(img, 0, 0, width * scale, height * scale);

  // Clean up
  URL.revokeObjectURL(url);

  // Convert canvas to blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      'image/png',
      1.0, // Maximum quality
    );
  });
}

/**
 * Convert a blob to base64 data URL
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} Base64 data URL
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert SVG element to base64 PNG data URL (convenience function)
 * @param {SVGElement} svgElement - The SVG DOM element to convert
 * @param {number} width - Output width in pixels
 * @param {number} height - Output height in pixels
 * @param {number} scale - Scale factor for higher resolution
 * @returns {Promise<string>} Base64 PNG data URL
 */
export async function svgToBase64Png(
  svgElement: SVGElement,
  width: number = 1200,
  height: number = 630,
  scale: number = 2,
): Promise<string> {
  const blob = await svgToPngBlob(svgElement, width, height, scale);
  return await blobToBase64(blob);
}
